import { Component, Input, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { formatDate } from '@angular/common';
import { Params     } from '@angular/router';
import { take, tap  } from 'rxjs/operators';

import {
  WorkingPeriodService,
  NotificationService,
  ActivityReportsCountersService,
  SessionStorageService,
  FailedErpService,
  HelperService,
  WebsocketService,
  OverviewTableService
} from '@shared/services';
import { TableHeaderEntryModel, TableBodyEntryModel, TableBodyCellNestedEntryModel, TableListIconModel } from '@shared/models';
import { ActivityReportSimplified } from '@shared/factories';

@Component({
  selector:     'activity-reports-table',
  templateUrl:  '../../table-entries.component.html',
  host: { class: 'am-flex-column bg-white b-radius-wide overflow' },
  styleUrls: ['./activity-reports-table.component.sass'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActivityReportsTableComponent {
  @Input() rows:             ActivityReportSimplified[];
  @Input() paginationId:     string;
  @Input() loading:          boolean;

  @Input() dashboardOnly:    boolean;

  @Input() isInternal:       boolean;
  @Input() isCustomer:       boolean;

  @Input() extraInfoSort:    string;
  @Output() headerCallback = new EventEmitter<void>();
  constructor(
    private activityReportsCountersService: ActivityReportsCountersService,
    private workingPeriodService:           WorkingPeriodService,
    private failedErpService:               FailedErpService,
    private helperService:                  HelperService,
    private overviewTableService:           OverviewTableService,
    private websocketService:               WebsocketService,
    private sessionStorageService:          SessionStorageService,
    private notificationService:            NotificationService
  ) { }

  headerCallbackHandler(res): void {
    this.headerCallback.emit(res);
  }

  getTableClass(): string {
    return 'activity-reports-table';
  }

  getOptionalValue(field: string): boolean {
    return this[field] || null;
  }

  prepareTableHeader(): TableHeaderEntryModel[] {
    return [
      { title: 'Mitarbeiter',            class: 'name-cell',          sort_by: ['external_employee_last_name', 'external_employee_first_name'], customerOnly: true                                     },
      { title: 'Mitarbeiter (Pers.Nr.)', class: 'name-cell',          sort_by: ['external_employee_last_name', 'external_employee_first_name'], internalOnly: true                                     },
      { title: 'Eingang',                class: 'date-cell',          sort_by: ['portal_review_first', 'created_at'],                           internalOnly: true, pageOnly: ['dashboard']            },
      { title: 'Eingang',                class: 'date-cell',          sort_by: ['archived_at'],                                                 internalOnly: true, pageOnly: ['failed-erp']           },

      { title: 'Erstellung',             class: 'date-cell',          sort_by: ['created_at'],                                                  internalOnly: true, pageOnly: ['archive']              },
      { title: 'Überprüfung',            class: 'realease-date-cell', sort_by: ['archived_at'],                                                 internalOnly: true, pageOnly: ['archive']              },

      { title: 'Eingang',                class: 'date-cell',          sort_by: ['created_at'],                                                  customerOnly: true, pageOnly: ['dashboard', 'archive'] },
      { title: 'Tempton',                class: 'realease-date-cell', sort_by: ['archived_at'],                                                 customerOnly: true, pageOnly: ['archive']              },

      { title: 'Einsatz',                class: 'title-cell',         sort_by: ['assignment_title']                                                                                                    },
      { title: 'Zeitraum',               class: 'week-cell',          sort_by: ['start_date'],                                                                                                         },
      { title: 'Zusatzinfo',             class: 'am-flex-center icons-cell relative', tooltipHtml: this.getExtraInfoTooltip(),    pageOnly: ['dashboard', 'archive'],
        sortOptions: [
          { class: 'am-flex-center font-small-icon icon-holiday',  sort_by: ['holiday',                       'created_at']                        },
          { class: 'am-flex-center font-small-icon icon-tick',     sort_by: ['checked_time',                  'created_at'], skip: this.isCustomer },
          { class: 'am-flex-center font-small-icon icon-camera',   sort_by: ['attachment',                    'created_at'], active: true          },
          { class: 'am-flex-center font-small-icon icon-car',      sort_by: ['mileage_money_report',          'created_at'], skip: this.isCustomer },
          { class: 'am-flex-center font-small-icon icon-document', sort_by: ['latest_customers_notification', 'created_at'], skip: this.isCustomer || this.paginationId !== 'dashboard' }
        ]
      },
      { title: 'Status',      class: 'status-cell',        sort_by: ['status'],                    internalOnly: true, pageOnly: [this.dashboardOnly ? 'dashboard' : 'skip'] },
      { title: 'Status',      class: 'status-cell',        sort_by: ['export_state'],              internalOnly: true, pageOnly: ['failed-erp']                              },
      { title: 'Aktion',      class: 'action-cell',                                                                    pageOnly: ['dashboard', 'archive']                    },
      { title: 'Aktion',      class: 'action-cell failed-erp',                                                         pageOnly: ['failed-erp']                              }
    ].filter(h => h.pageOnly ? h.pageOnly.find(p => p === this.paginationId) : true);
  }

  private getExtraInfoTooltip(): string {
    return `
      <div class="web-tooltip white medium bottom left-sm">
        <span class="tooltip-title">Folgende Parameter sind in dieser Spalte sichtbar:</span>
        <ul class="tooltip-list">
          ${this.isInternal ? '<li><span class="icon-tick"></span><span>Intern geprüft</span></li>' : ''}
          <li><span class="icon-camera"></span><span>Foto Tätigkeitsnachweis</span></li>
          ${this.isInternal ? '<li><span class="icon-car"></span><span>Kilometergeld</span></li>'   : ''}
          <li><span class="icon-holiday"></span><span>Feiertag(e)</span></li>
          ${this.isInternal && this.paginationId === 'dashboard' ? '<li><span class="icon-document"></span><span>TN-Beleg</span></li>' : ''}
        </ul>
      </div>
    `;
  }

  private collectSplitReports(list: ActivityReportSimplified[], wp: ActivityReportSimplified, index: number): string {
    let borders = '', sideBorders = 'bl-grey ml-xs-laptop pl-xxs-laptop';
    let topBorder    = 'btl-radius bt-grey';
    let bottomBorder = 'bbl-radius';

    if (list[index+1]) borders += this.connectedReport(wp, list[index+1]) ? `${sideBorders} ${topBorder} `    : '';
    if (list[index-1]) borders += this.connectedReport(wp, list[index-1]) ? `${sideBorders} ${bottomBorder} ` : '';
    if (list[index+2] && this.connectedReport(list[index+1], list[index+2])) borders += 'bb-none-important'
    return borders;
  }

  private connectedReport(a: ActivityReportSimplified, b: ActivityReportSimplified): boolean {
    return a.split_child_id  && b.id === a.split_child_id ||
           a.split_parent_id && b.id === a.split_parent_id;
  }

  private preparePreload(): TableBodyEntryModel[] {
    return new Array(10).fill({
      cells: [
        this.overviewTableService.getExternalEmployeeCellPlaceholder(),
        { xs_label: ' ',  class: 'date-cell',          value: ' ',  pageOnly: ['dashboard', this.isCustomer ? 'archive' : 'skip']                        },
        { xs_label: ' ',  class: `date-cell`,          value: ' ',  pageOnly: ['failed-erp']                                                             },
        { xs_label: ' ',  class: 'date-cell',          value: ' ',  pageOnly: ['archive'], internalOnly: true                                            },
        { xs_label: ' ',  class: 'realease-date-cell', value: ' ',  pageOnly: ['archive'], internalOnly: true                                            },
        { xs_label: ' ',  class: 'realease-date-cell', value: ' ',  pageOnly: ['archive'], customerOnly: true                                            },
       ...this.overviewTableService.getAssignmentCellsPlaceholder(),
          this.overviewTableService.getWeekCellPlaceholder(),
        { xs_label: null, class: 'icons-cell  xs-hide',             pageOnly: ['dashboard', 'archive'], xs_icons: new Array(5).fill({})                  },
        { xs_label: null, class: 'status-cell xs-hide',             pageOnly: [this.dashboardOnly ? 'dashboard' : 'skip', 'failed-erp'], children: [{ class: 'status-icon loading-animate' }], internalOnly: true },
        { xs_label: ' ',  class: 'extra-cell  xs-show-table'                                                                                             },
        { class: 'action-cell',                                     pageOnly: ['dashboard', 'archive'], buttons: [{ class: 'action-cell-button'        }]},
        { class: 'action-cell failed-erp',                          pageOnly: ['failed-erp'],           buttons: [{ class: 'custom-action-cell-button' },
                                                                                                                  { class: 'action-cell-button'        }]}
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    });
  }

  prepareTableBody(): TableBodyEntryModel[] {
    if (this.loading) return this.preparePreload();
    return this.rows.map((wp: ActivityReportSimplified, index): TableBodyEntryModel => ({
      read_only: this.isInternal && !wp.belongsToInternalLocations,
      extra_styles: this.collectSplitReports(this.rows, wp, index),
      cells: [
        this.overviewTableService.getExternalEmployeeCell(wp.external_employee, { statusDot: this.isInternal && this.dashboardOnly && this.overviewTableService.getStatus(wp)?.color }),
        this.overviewTableService.getActivityReportDateCell(wp),
        { xs_label: 'Eingang',     class: 'date-cell',          value: this.toString(wp.archived_at),                            pageOnly: ['failed-erp'] },
        { xs_label: 'Erstellung',  class: 'date-cell',          value: this.toString(wp.created_at),         internalOnly: true, pageOnly: ['archive']    },
        { xs_label: 'Überprüfung', class: 'realease-date-cell', value: this.finalStateDateString(wp) || '-', internalOnly: true, pageOnly: ['archive']    },
        { xs_label: 'Tempton',     class: 'realease-date-cell', value: this.finalStateDateString(wp) || '-', customerOnly: true, pageOnly: ['archive'],
          children: [{
            skip: !this.finalStateDateString(wp),
            class: `web-tooltip bottom short ${this.finalState(wp)?.color}`,
            children: [{
              class: "tooltip-title",
              value: this.finalState(wp)?.label
            }]
          }],
          valueClass: `color-${this.finalState(wp)?.color}`
        },
        ...this.overviewTableService.getAssignmentCells(wp.assignment),
        this.overviewTableService.getWeekCell(wp.calendar_week, wp.date),
        { xs_label: null, class: 'icons-cell xs-hide', pageOnly: ['dashboard', 'archive'],
          children: [{
            class: 'am-flex-center font-icon gap-xxs',
            children: this.helperService.parceTableListIconsWeb(this.getIcons(wp))
          }]
        },
        this.overviewTableService.getStatusCell(wp, { dashboardOnly: this.dashboardOnly, internalOnly: true, paginationId: this.paginationId, pageOnly: [this.dashboardOnly ? 'dashboard' : 'skip', 'failed-erp'] }),
        { xs_label: 'Zusatzinfo',  class: 'extra-cell xs-show-table', xs_icons: this.getIconsRowMobile(wp) },
        { class: 'action-cell', pageOnly: ['dashboard', 'archive'],
          buttons: [{
            class: 'action-cell-button',
            label: this.getActionButtonName(wp),
            routerLink: ['/time-tracking/wp-details', wp.id],
            routerLinkState: this.getFastNavigationData()
          }]
        },
        { class: 'action-cell failed-erp', pageOnly: ['failed-erp'],
          buttons: [
            { class: 'blue-thin-border custom-action-cell-button',
              label: 'Ansehen',
              routerLink: ['/time-tracking/wp-details', wp.id],
              routerLinkState: this.getFastNavigationData()
            },
            { class: 'action-cell-button',
              skip: wp.export_state !== 'failed_export' || !wp.belongsToInternalLocations,
              label: 'Erledigen',
              click: () => this.manuallyResolve(wp)
            }
          ]
        }
      ].filter(c => c.pageOnly && c.pageOnly.filter(p => p).length ? c.pageOnly.filter(p => p).find(p => p === this.paginationId) : true)
    }));
  }

  private getIcons(ar: ActivityReportSimplified): TableListIconModel[] {
    let icons = [
      { show: !!ar.checked_time_at,         icon: 'icon-tick',     color: 'white',         tooltipTexts: ['Intern geprüft'],        internalOnly: true },
      { show: !!ar.holidays?.length,        icon: 'icon-holiday',  color: 'orange-border', tooltipTexts: ar.holidays                                   },
      { show: !!ar.with_attachment,         icon: 'icon-camera',   color: 'white',         tooltipTexts: ['Foto Tätigkeitsnachweis']                   },
      { show: !!ar.mileage_money_report_id, icon: 'icon-car',      color: 'white',         tooltipTexts: ['Kilometergeld'],         internalOnly: true,
        linkTree: ['/time-tracking/wp-details', ar.id, 'wp-km', ar.mileage_money_report_id],
        linkState: this.getFastNavigationData()
      },
    ];

    if (this.paginationId === 'dashboard') {
      let customerNotificationLabel = ar?.latestCustomersNotification?.created_at ? `TN-Beleg vom ${formatDate(ar.latestCustomersNotification.created_at, 'dd.MM.yyyy', 'de')}` : '-';
      icons.push({ show: !!ar.latestCustomersNotification, icon: 'icon-document', color: 'white', tooltipTexts: [customerNotificationLabel], internalOnly: true });
    }

    return icons;
  }

  private getIconsRowMobile(ar: ActivityReportSimplified): TableBodyCellNestedEntryModel[] {
    let icons: any = this.getIcons(ar).filter(i => i.show);
    if (icons.length) return this.helperService.parceTableListIconsMobile(icons);
    return [{ value: '-' }];
  }

  private getFastNavigationData(): Params {
    return ({
      data: this.rows.map(r => ({
        id: r.id,
        page: this.sessionStorageService.pageNumberValue
      }))
    });
  }

  private finalStateDateString(wp: ActivityReportSimplified): string {
    return wp.archived_at ? formatDate(wp.archived_at, 'dd.MM.yyyy', 'de') : null;
  }

  private finalState(wp: ActivityReportSimplified) {
    if (wp.approvedByInternal) return { color: 'green', label: 'Von Tempton überprüft'  };
    if (wp.rejectedByInternal) return { color: 'red',   label: 'Von Tempton archiviert' };
  }

  private getActionButtonName(wp: ActivityReportSimplified): string {
    if (this.isCustomer) return this.getActionButtonNameCustomer(wp);
    if (this.isInternal) return this.getActionButtonNameInternal(wp);
  }

  private getActionButtonNameCustomer(wp: ActivityReportSimplified): string {
    return wp.readOnlyCustomer ? 'Ansehen' : 'Bearbeiten';
  }
  private getActionButtonNameInternal(wp: ActivityReportSimplified): string {
    return wp.readOnlyInternal ? 'Ansehen' : 'Bearbeiten';
  }

  manuallyResolve(wp: ActivityReportSimplified): void {
    this.notificationService.wait();
    this.workingPeriodService.manuallyResolveWorkingPeriod(wp.id).pipe(
      take(1),
      tap(() => this.sessionStorageService.failedResolved = 'ar')
    ).subscribe(
      res => this.reloadCounters(),
      error => this.notificationService.alert(error.message || error)
    );
  }

  private reloadCounters(): void {
    if (!this.websocketService.wsOnline()) {
      this.activityReportsCountersService.reloadCounters();
      this.failedErpService.forceReload();
    }
  }

  private toString(date: Date): string {
    return formatDate(date, 'dd.MM.yyyy', 'de');
  }

}
